<script lang="ts">
  import clsx from 'clsx';
  import { Text } from '@athena-lib/Text';
  import { getColorContext } from '@athena-lib/container-context.svelte';
  import { objectEntries } from '@athena-lib/object-utils';
  import { type Responsive } from '@athena-lib/responsive-utils';
  import type { CheckboxProps } from './types';

  let {
    onClick,
    checked = $bindable(false),
    disabled,
    boxSize,
    textSize,
    spacing,
    id,
    label,
    classes,
  }: CheckboxProps = $props();

  const sizeClass = $derived.by(() => {
    const sizeClasses: Record<string, string> = {
      small: clsx('h-3', 'w-3'),
      medium: clsx('h-5', 'w-5'),
      large: clsx('h-7', 'w-7'),
    };

    return sizeClasses[boxSize ?? 'medium'];
  });

  const spaceClass = $derived.by(() => {
    const spaceClasses: Record<string, string> = {
      small: clsx('ml-1'),
      medium: clsx('ml-1'),
      large: clsx('ml-2'),
    };

    return spaceClasses[spacing ?? 'medium'];
  });

  const textClass = $derived.by(() => {
    const textClasses: Record<string, string> = {
      small: clsx('text-sm'),
      medium: clsx('text-md'),
      large: clsx('text-lg'),
    };

    return textClasses[textSize ?? 'medium'];
  });
  const contextBackground = getColorContext();
  const colors = $derived.by(() => {
    const responsiveBackground: Responsive<string> = {
      lg:
        contextBackground.value.lg === 'dark'
          ? 'lg:bg-st-white lg:checked:bg-st-white'
          : 'lg:bg-st-black lg:checked:bg-st-black',
      md:
        contextBackground.value.md === 'dark'
          ? 'md:bg-st-white md:checked:bg-st-white'
          : 'md:bg-st-black md:checked:bg-st-black',
      sm:
        contextBackground.value.sm === 'dark'
          ? 'bg-st-white checked:bg-st-white'
          : 'bg-st-black checked:bg-st-black',
    };

    const responsiveCheckboxColor: Responsive<string> = {
      lg: contextBackground.value.lg === 'dark' ? 'lg:dark' : 'lg:light',
      md: contextBackground.value.md === 'dark' ? 'md:dark' : 'md:light',
      sm: contextBackground.value.sm === 'dark' ? 'dark' : 'light',
    };
    return {
      bg: objectEntries(responsiveBackground)
        .map(([_, value]) => value)
        .join(' '),
      checkbox: objectEntries(responsiveCheckboxColor)
        .map(([_, value]) => value)
        .join(' '),
    };
  });
</script>

<label class={clsx(colors.checkbox, 'inline-flex items-center')} for={id}>
  <input
    type="checkbox"
    {id}
    class={clsx(
      classes,
      colors.bg,
      'form-checkbox',
      sizeClass,
      `appearance-none rounded focus:ring-1`,
    )}
    onclick={onClick}
    bind:checked
    {disabled}
  />
  <Text element="span" class={clsx(spaceClass, textClass)}>{label}</Text>
</label>

<style>
  .dark .form-checkbox:checked[type='checkbox'] {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='black' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
    background-origin: border-box;
  }
  .light .form-checkbox:checked[type='checkbox'] {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='rgb(255,233,52)' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
    background-origin: border-box;
  }
  .form-checkbox:disabled[type='checkbox'] {
    opacity: 50%;
    cursor: not-allowed;
  }
</style>
